import React, { useState, useEffect, useRef, useCallback } from 'react';
import {
  Box,
  Typography,
  Button,
  Alert,
  Snackbar,
  CircularProgress
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  ArrowForward as ArrowForwardIcon,
  Check as CheckIcon,
  Store as StoreIcon,
  ContactMail as ContactIcon,
  LocationOn as LocationIcon,
  Schedule as BusinessHoursIcon,
  RateReview as ReviewIcon
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate, useParams } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import StoreService from '../../services/storeService';
import { getFunctionalItem, setFunctionalItem } from '../../utils/functionalStorage';
import { useAppData } from '../../context/AppDataContext';
import ManagementPageLayout, { ManagementPageCard } from '../../components/layout/ManagementPageLayout';
import ManagementPageSkeleton from '../../components/layout/ManagementPageSkeleton';
import StoreBasicInfo from '../../components/store-registration/StoreBasicInfo';
import StoreContactInfo from '../../components/store-registration/StoreContactInfo';
import StoreLocationInfo from '../../components/store-registration/StoreLocationInfo';
import StoreBusinessHoursInfo from '../../components/store-registration/StoreBusinessHoursInfo';
import StoreReview from '../../components/store-registration/StoreReview';
import ConfirmDialog from '../../components/common/ConfirmDialog';
import requestDeduplicator from '../../utils/requestDeduplicator';
import { DEFAULT_CURRENCY } from '../../utils/currencyUtils';
import { getDefaultTimeZone } from '../../utils/timeZoneUtils';
import {
  composePhoneValue,
  DEFAULT_PHONE_COUNTRY_CODE,
  normalizeFormPhoneFields,
  splitPhoneValue,
} from '../../utils/phoneUtils';
import { DEFAULT_LANGUAGE, DEFAULT_STORE_SUPPORTED_LANGUAGES, SUPPORTED_LANGUAGE_CODES, normalizeLanguage } from '../../constants/Languages';
import { getSlugFormatErrorKey } from '../../utils/storeSlugUtils';
import { BRAND, brandAlpha } from '../../theme/brandTokens';

const StoreUpdatePage = () => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { storeId } = useParams();
  const { refreshStores } = useAppData();
  const [activeStep, setActiveStep] = useState(0);
  const [formData, setFormData] = useState({
    name: '',
    slug: '',
    currency: DEFAULT_CURRENCY,
    supportedLanguages: [...DEFAULT_STORE_SUPPORTED_LANGUAGES],
    storeLanguage: DEFAULT_LANGUAGE,
    about: Object.fromEntries(SUPPORTED_LANGUAGE_CODES.map(code => [code, ''])),
    email: '',
    phoneCountryCode: DEFAULT_PHONE_COUNTRY_CODE,
    phoneNumber: '',
    website: '',
    vatNumber: '',
    googleReviewUrl: '',
    address: '',
    door: '',
    additionalInfo: '',
    city: '',
    state: '',
    zipCode: '',
    country: '',
    timeZone: getDefaultTimeZone(),
    slotInterval: 'Thirty',
    businessHours: {
      monday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      tuesday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      wednesday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      thursday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      friday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      saturday: { closed: false, periods: [{ from: '09:00', to: '16:00' }] },
      sunday: { closed: true, periods: [{ from: '09:00', to: '18:00' }] }
    },
    disabled: false
  });
  const [errors, setErrors] = useState({});
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const [slugChangeDialogOpen, setSlugChangeDialogOpen] = useState(false);
  const isCompletingUpdate = useRef(false);
  const initialFormDataRef = useRef(null);

  // Storage keys for caching
  const CACHE_KEY = `bf_store_update_cache_${storeId}`;
  const INITIAL_DATA_KEY = `bf_store_update_initial_${storeId}`;

  // Transform opening hours from API format to form format
  const transformOpeningHours = useCallback((openingHours) => {
    const defaultHours = {
      monday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      tuesday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      wednesday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      thursday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      friday: { closed: false, periods: [{ from: '09:00', to: '18:00' }] },
      saturday: { closed: false, periods: [{ from: '09:00', to: '16:00' }] },
      sunday: { closed: true, periods: [{ from: '09:00', to: '18:00' }] }
    };

    if (!openingHours || openingHours.length === 0) {
      return defaultHours;
    }

    const transformedHours = { ...defaultHours };
    
    openingHours.forEach(dayHours => {
      const dayKey = dayHours.weekDay.toLowerCase();
      if (transformedHours[dayKey]) {
        if (dayHours.periods && dayHours.periods.length > 0) {
          transformedHours[dayKey] = {
            closed: false,
            periods: dayHours.periods.map(p => ({ from: p.from, to: p.to }))
          };
        } else {
          transformedHours[dayKey] = {
            closed: true,
            periods: [{ from: '09:00', to: '18:00' }]
          };
        }
      }
    });

    return transformedHours;
  }, []);

  // Load store data on component mount
  useEffect(() => {
    const loadStore = async () => {
      const requestKey = `getStore-${storeId}`;
      
      return requestDeduplicator.deduplicate(requestKey, async () => {
        try {
          setLoading(true);
          const storeData = await StoreService.getStore(storeId);
          
          const initialFormData = {
            name: storeData.name || '',
            slug: storeData.slug || '',
            currency: storeData.currency || DEFAULT_CURRENCY,
            supportedLanguages: storeData.supportedLanguages
              ? storeData.supportedLanguages.map(lang => lang.toLowerCase())
              : [...DEFAULT_STORE_SUPPORTED_LANGUAGES],
            storeLanguage: normalizeLanguage(storeData.storeLanguage || storeData.supportedLanguages?.[0] || DEFAULT_LANGUAGE),
            about: storeData.about
              ? Object.fromEntries(SUPPORTED_LANGUAGE_CODES.map(code => [
                  code,
                  storeData.about[code.toUpperCase()] || ''
                ]))
              : Object.fromEntries(SUPPORTED_LANGUAGE_CODES.map(code => [code, ''])),
            email: storeData.contacts?.find(c => c.type === 'Email')?.value || '',
            ...(() => {
              const phone = splitPhoneValue(storeData.contacts?.find(c => c.type === 'Phone1')?.value || '');
              return {
                phoneCountryCode: phone.countryCode,
                phoneNumber: phone.number,
              };
            })(),
            website: storeData.contacts?.find(c => c.type === 'Website')?.value || '',
            vatNumber: storeData.vatNumber || storeData.VatNumber || '',
            googleReviewUrl: storeData.googleReviewUrl || storeData.GoogleReviewUrl || '',
            address: storeData.address?.street || '',
            door: storeData.address?.door || '',
            additionalInfo: storeData.address?.additionalInfo || '',
            city: storeData.address?.city || '',
            state: storeData.address?.state || '',
            zipCode: storeData.address?.zipCode || '',
            country: storeData.address?.country || '',
            timeZone: storeData.timeZone || getDefaultTimeZone(),
            slotInterval: storeData.slotInterval || 'Thirty',
            businessHours: transformOpeningHours(storeData.openingHours),
            disabled: storeData.disabled || false,
          };

          // Store initial data for rollback
          initialFormDataRef.current = initialFormData;
          try {
            setFunctionalItem(INITIAL_DATA_KEY, JSON.stringify(initialFormData));
          } catch (e) {
            console.warn('Failed to store initial data:', e);
          }

          // Try to load cached data, otherwise use initial data
          let cachedData = null;
          try {
            const cached = getFunctionalItem(CACHE_KEY);
            if (cached) {
              cachedData = JSON.parse(cached);
              delete cachedData.services;
            }
          } catch (e) {
            console.warn('Failed to load cached data:', e);
          }

          setFormData(normalizeFormPhoneFields(cachedData ? {
            ...initialFormData,
            ...cachedData,
            timeZone: cachedData.timeZone || initialFormData.timeZone,
            storeLanguage: normalizeLanguage(cachedData.storeLanguage || initialFormData.storeLanguage),
            slug: cachedData.slug || initialFormData.slug,
          } : initialFormData));
        } catch (error) {
          console.error('Error loading store:', error);
          setSnackbar({
            open: true,
            message: t('storeUpdate.error.loading'),
            severity: 'error'
          });
          throw error; // Re-throw to let deduplicator handle it
        } finally {
          setLoading(false);
        }
      });
    };

    loadStore();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [storeId, t, transformOpeningHours]);

  // Cache form data on changes (but not during initial load)
  useEffect(() => {
    if (loading || !initialFormDataRef.current) return;
    
    try {
      setFunctionalItem(CACHE_KEY, JSON.stringify(formData));
    } catch (e) {
      console.warn('Failed to cache form data:', e);
    }
  }, [formData, loading, CACHE_KEY, INITIAL_DATA_KEY, t]);

  useEffect(() => {
    return () => {
      if (isCompletingUpdate.current) {
        return;
      }

      try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(INITIAL_DATA_KEY);
      } catch (e) {
        console.warn('Failed to clear store update cache:', e);
      }
    };
  }, [CACHE_KEY, INITIAL_DATA_KEY]);

  // Rollback handler - restore initial data
  const handleRollback = () => {
    try {
      // Clear cache
      localStorage.removeItem(CACHE_KEY);
      
      // Restore initial data
      const initialJson = getFunctionalItem(INITIAL_DATA_KEY);
      if (initialJson) {
        const initialData = JSON.parse(initialJson);
        setFormData(initialData);
        initialFormDataRef.current = initialData;
      } else if (initialFormDataRef.current) {
        setFormData(initialFormDataRef.current);
      }
      
      // Reset to first step
      setActiveStep(0);
      
      setSnackbar({
        open: true,
        message: t('storeUpdate.discardSuccess') || 'Changes discarded. Form reset to original data.',
        severity: 'info'
      });
    } catch (e) {
      console.error('Failed to discard changes:', e);
      setSnackbar({
        open: true,
        message: t('storeUpdate.discardError') || 'Failed to discard changes.',
        severity: 'error'
      });
    }
  };


  const steps = [
    {
      label: t('storeRegistrationSteps.step1.title'),
      description: t('storeRegistrationSteps.step1.description'),
      icon: <StoreIcon />
    },
    {
      label: t('storeRegistrationSteps.step2.title'),
      description: t('storeRegistrationSteps.step2.description'),
      icon: <ContactIcon />
    },
    {
      label: t('storeRegistrationSteps.step3.title'),
      description: t('storeRegistrationSteps.step3.description'),
      icon: <LocationIcon />
    },
    {
      label: t('storeRegistrationSteps.step4.title'),
      description: t('storeRegistrationSteps.step4.description'),
      icon: <BusinessHoursIcon />
    },
    {
      label: t('storeRegistrationSteps.step5.title'),
      description: t('storeRegistrationSteps.step5.description'),
      icon: <ReviewIcon />
    }
  ];

  const lastValidatedStepIndex = steps.length - 2;

  const getStepErrors = (step) => {
    const newErrors = {};

    switch (step) {
      case 0: // Basic Info
        if (!formData.name.trim()) {
          newErrors.name = t('storeRegistration.validation.nameRequired');
        }
        const slugErrorKey = getSlugFormatErrorKey(formData.slug);
        if (slugErrorKey) {
          newErrors.slug = t(slugErrorKey);
        }
        if (!formData.timeZone) {
          newErrors.timeZone = t('storeRegistration.validation.timeZoneRequired');
        }
        if (formData.supportedLanguages.length === 0) {
          newErrors.languages = t('storeRegistration.validation.languagesRequired');
        }
        if (!formData.storeLanguage) {
          newErrors.storeLanguage = t('storeRegistration.validation.storeLanguageRequired');
        } else if (!formData.supportedLanguages.includes(formData.storeLanguage)) {
          newErrors.storeLanguage = t('storeRegistration.validation.storeLanguageMustBeSupported');
        }
        const missingAbout = formData.supportedLanguages.filter(
          lang => !formData.about[lang] || !formData.about[lang].trim()
        );
        if (missingAbout.length > 0) {
          newErrors.about = 'storeRegistration.validation.aboutRequired';
        }
        break;

      case 1: // Contact Info
        if (!formData.email.trim()) {
          newErrors.email = t('storeRegistration.validation.emailRequired');
        } else if (!/\S+@\S+\.\S+/.test(formData.email)) {
          newErrors.email = t('storeRegistration.validation.emailInvalid');
        }
        if (!formData.phoneNumber.trim()) {
          newErrors.phoneNumber = t('storeRegistration.validation.phoneRequired');
        }
        break;

      case 2: // Location Info
        if (!formData.address.trim()) {
          newErrors.address = t('storeRegistration.validation.addressRequired');
        }
        if (!formData.city.trim()) {
          newErrors.city = t('storeRegistration.validation.cityRequired');
        }
        if (!formData.state.trim()) {
          newErrors.state = t('storeRegistration.validation.stateRequired');
        }
        if (!formData.zipCode.trim()) {
          newErrors.zipCode = t('storeRegistration.validation.zipCodeRequired');
        }
        if (!formData.country.trim()) {
          newErrors.country = t('storeRegistration.validation.countryRequired');
        }
        break;

      case 3: // Business hours
        // Validate business hours - at least one day must be enabled
        const enabledDays = Object.values(formData.businessHours).filter(hours => !hours.closed);
        if (enabledDays.length === 0) {
          newErrors.businessHours = t('storeRegistration.validation.businessHoursRequired');
        }
        break;

      default:
        break;
    }

    return newErrors;
  };

  const validateStep = (step) => {
    const newErrors = getStepErrors(step);
    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const validateStepsAndFocusFirstInvalid = (stepIndexes) => {
    for (const stepIndex of stepIndexes) {
      const stepErrors = getStepErrors(stepIndex);

      if (Object.keys(stepErrors).length > 0) {
        setErrors(stepErrors);
        setActiveStep(stepIndex);
        return false;
      }
    }

    return true;
  };

  const handleNext = () => {
    if (validateStep(activeStep)) {
      setActiveStep((prevActiveStep) => prevActiveStep + 1);
    }
  };

  const handleBack = () => {
    if (activeStep <= lastValidatedStepIndex && !validateStep(activeStep)) {
      return;
    }

    setActiveStep((prevActiveStep) => prevActiveStep - 1);
  };

  const handleStepClick = (stepIndex) => {
    if (saving || stepIndex === activeStep) {
      return;
    }

    if (stepIndex > activeStep) {
      const lastStepBeforeTarget = Math.min(stepIndex - 1, lastValidatedStepIndex);
      const stepsToValidate = Array.from(
        { length: lastStepBeforeTarget + 1 },
        (_, index) => index
      );

      if (!validateStepsAndFocusFirstInvalid(stepsToValidate)) {
        return;
      }
    } else if (activeStep <= lastValidatedStepIndex && !validateStep(activeStep)) {
      return;
    }

    setActiveStep(stepIndex);
  };

  const handleSubmit = async () => {
    const stepsToValidate = Array.from({ length: lastValidatedStepIndex + 1 }, (_, index) => index);

    if (!validateStepsAndFocusFirstInvalid(stepsToValidate)) {
      return;
    }

    const originalSlug = initialFormDataRef.current?.slug || '';
    if ((formData.slug || '') !== originalSlug) {
      setSlugChangeDialogOpen(true);
      return;
    }

    await performUpdate();
  };

  const handleConfirmSlugChange = async () => {
    setSlugChangeDialogOpen(false);
    await performUpdate();
  };

  const performUpdate = async () => {
    setSaving(true);
    isCompletingUpdate.current = true;
    try {
      // Convert supported languages to enum values
      const supportedLanguages = formData.supportedLanguages.map(lang => lang.toUpperCase());
      const storeLanguage = (formData.storeLanguage || formData.supportedLanguages[0] || DEFAULT_LANGUAGE).toUpperCase();

      // Convert about to enum keys - only for supported languages
      const aboutWithEnumKeys = {};
      formData.supportedLanguages.forEach(lang => {
        aboutWithEnumKeys[lang.toUpperCase()] = formData.about[lang] || '';
      });

      // Build contacts array
      const contacts = [
        {
          type: 'Email', // Email = 3
          value: formData.email,
          label: 'Primary Email'
        },
        {
          type: 'Phone1', // Phone1 = 1
          value: composePhoneValue(formData.phoneCountryCode, formData.phoneNumber),
          label: 'Primary Phone'
        }
      ];

      // Add website contact if provided
      if (formData.website && formData.website.trim()) {
        contacts.push({
          type: 'Website', // Website = 4
          value: formData.website,
          label: 'Website'
        });
      }

      // Convert business hours to enum values
      const dayEnumMap = {
        'sunday': 'Sunday',
        'monday': 'Monday',
        'tuesday': 'Tuesday',
        'wednesday': 'Wednesday',
        'thursday': 'Thursday',
        'friday': 'Friday',
        'saturday': 'Saturday'
      };

      const updateData = {
        name: formData.name,
        slug: formData.slug,
        currency: formData.currency || DEFAULT_CURRENCY,
        about: aboutWithEnumKeys,
        address: {
          street: formData.address,
          door: formData.door,
          additionalInfo: formData.additionalInfo,
          city: formData.city,
          state: formData.state,
          zipCode: formData.zipCode,
          country: formData.country
        },
        contacts: contacts,
        timeZone: formData.timeZone || getDefaultTimeZone(),
        openingHours: Object.entries(formData.businessHours)
          .filter(([day, hours]) => !hours.closed && hours.periods?.length > 0)
          .map(([day, hours]) => ({
            weekDay: dayEnumMap[day.toLowerCase()],
            periods: hours.periods.map(p => ({ from: p.from, to: p.to }))
          })),
        supportedLanguages: supportedLanguages,
        storeLanguage: storeLanguage,
        slotInterval: formData.slotInterval || 'Thirty',
        disabled: formData.disabled,
        googleReviewUrl: formData.googleReviewUrl?.trim() || null,
        vatNumber: formData.vatNumber?.trim() || null
      };

      await StoreService.updateStore(storeId, updateData);
      
      // Clear cache on successful save
      try {
        localStorage.removeItem(CACHE_KEY);
        localStorage.removeItem(INITIAL_DATA_KEY);
      } catch (e) {
        console.warn('Failed to clear cache:', e);
      }
      
      // Refresh global stores data
      await refreshStores();
      
      setSnackbar({
        open: true,
        message: t('storeUpdate.success'),
        severity: 'success'
      });
      setTimeout(() => {
        navigate('/management/stores');
      }, 2000);
    } catch (error) {
      setSnackbar({
        open: true,
        message: error.message || t('storeUpdate.error'),
        severity: 'error'
      });
    } finally {
      setSaving(false);
    }
  };

  const updateFormData = (updates) => {
    setFormData(prev => {
      const newFormData = { ...prev, ...updates };
      const nextSupportedLanguages = newFormData.supportedLanguages || [];

      if (nextSupportedLanguages.length > 0 && !nextSupportedLanguages.includes(newFormData.storeLanguage)) {
        newFormData.storeLanguage = nextSupportedLanguages[0];
      }

      if (nextSupportedLanguages.length === 0) {
        newFormData.storeLanguage = '';
      }
      
      return newFormData;
    });
  };

  const handleSnackbarClose = () => {
    setSnackbar(prev => ({ ...prev, open: false }));
  };

  const renderStepContent = (step) => {
    switch (step) {
      case 0:
        return <StoreBasicInfo formData={formData} updateFormData={updateFormData} errors={errors} setErrors={setErrors} originalSlug={initialFormDataRef.current?.slug} />;
      case 1:
        return <StoreContactInfo formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 2:
        return <StoreLocationInfo formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 3:
        return <StoreBusinessHoursInfo formData={formData} updateFormData={updateFormData} errors={errors} />;
      case 4:
        return <StoreReview formData={formData} onRollback={handleRollback} showRollback={true} loading={loading} saving={saving} />;
      default:
        return null;
    }
  };

  if (loading) {
    return (
      <ManagementPageSkeleton
        pageTitle={t('storeUpdate.title')}
        pageSubtitle={t('storeUpdate.subtitle')}
        variant="form"
        contentSx={{
          maxWidth: { lg: '960px' },
          width: { xs: '100%', lg: '960px' }
        }}
      />
    );
  }

  return (
    <ManagementPageLayout
      id="store-update-page"
      containerId="store-update-header"
      titleId="store-update-title"
      subtitleId="store-update-subtitle"
      pageTitle={formData.name || t('storeUpdate.title')}
      pageSubtitle={t('storeUpdate.subtitle')}
      contentId="store-update-main-content"
      contentSx={{
        maxWidth: { lg: '960px' },
        width: { xs: '100%', lg: '960px' }
      }}
    >
        <ManagementPageCard id="store-update-card">
          {/* Custom Stepper */}
          <Box id="store-update-stepper-container" sx={{
            background: BRAND.background,
            borderBottom: `1px solid ${BRAND.line}`,
            p: 4
          }}>
            <Box sx={{
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'stretch', // This will make all items stretch to the same height
              position: 'relative'
            }}>
              {steps.map((step, index) => (
                <Box key={index} sx={{
                  display: 'flex',
                  flexDirection: 'column',
                  alignItems: 'center',
                  flex: 1,
                  position: 'relative',
                  overflow: 'visible',
                  justifyContent: 'flex-start' // Align content to top
                }}>
                  {/* Step Circle */}
                  <Box
                    component="button"
                    type="button"
                    id={`store-update-step-icon-${index + 1}`}
                    onClick={() => handleStepClick(index)}
                    aria-label={`Go to ${step.label}`}
                    sx={{
                      width: 50,
                      height: 50,
                      borderRadius: '50%',
                      background: activeStep >= index ? BRAND.accent : BRAND.surface,
                      color: activeStep >= index ? BRAND.onAccent : BRAND.textMuted,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      fontSize: '1.4rem',
                      fontWeight: 'bold',
                      border: `3px solid ${activeStep >= index ? BRAND.accent : BRAND.line}`,
                      boxShadow: 'none',
                      transition: 'all 0.3s ease',
                      zIndex: 2,
                      position: 'relative',
                      cursor: saving ? 'default' : 'pointer',
                      padding: 0,
                      outline: 'none',
                      appearance: 'none',
                      WebkitAppearance: 'none',
                      '&:hover': saving ? undefined : {
                        transform: 'translateY(-1px)',
                        boxShadow: `0 0 0 3px ${brandAlpha('accent', 0.16)}`,
                      },
                      '&:focus-visible': {
                        boxShadow: `0 0 0 3px ${brandAlpha('accent', 0.35)}`,
                      },
                      '&:disabled': {
                        cursor: 'default',
                      }
                    }}
                  >
                    {activeStep > index ? <CheckIcon /> : step.icon}
                  </Box>

                  {/* Step Label */}
                  <Typography
                    id={`store-update-step-label-${index + 1}`}
                    sx={{
                      color: activeStep >= index ? BRAND.text : BRAND.textMuted,
                      fontWeight: activeStep >= index ? 700 : 500,
                      fontSize: '0.9rem',
                      mt: 1.5,
                      textAlign: 'center',
                      display: { xs: 'none', sm: 'block' },
                      lineHeight: '1.25rem',
                      width: '100%',
                      flex: 1 // This will make the label take up remaining space
                    }}
                  >
                    {step.label}
                  </Typography>

                  {/* Connector Line */}
                  {index < steps.length - 1 && (
                    <Box sx={{
                      position: 'absolute',
                      top: '25px',
                      left: 'calc(50% + 25px)',
                      right: 'calc(-50% + 25px)',
                      height: '3px',
                      background: activeStep > index ? BRAND.accent : BRAND.line,
                      zIndex: 1
                    }} />
                  )}
                </Box>
              ))}
            </Box>
          </Box>

          {/* Step Content */}
          <Box id="store-update-step-content" sx={{ p: { xs: 1, sm: 4, md: 6 } }}>
            <AnimatePresence mode="wait">
              <motion.div
                key={activeStep}
                id={`store-update-step-${activeStep + 1}-content`}
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.3 }}
              >
                {renderStepContent(activeStep)}
              </motion.div>
            </AnimatePresence>

            {/* Navigation */}
            <Box
              id="store-update-navigation"
              sx={{
                display: 'flex',
                justifyContent: 'space-between',
                mt: 4,
                pt: 3,
                borderTop: '1px solid rgba(0,0,0,0.1)'
              }}
            >
              <Box sx={{ display: 'flex', gap: 2 }}>
                <Button
                  id="store-update-back-button"
                  variant="outlined"
                  onClick={handleBack}
                  disabled={activeStep === 0}
                  startIcon={<ArrowBackIcon />}
                  sx={{
                    borderRadius: 2,
                    px: 4,
                    py: 1.5,
                    borderColor: BRAND.accent,
                    color: BRAND.accentText,
                    fontWeight: 600,
                    '&:hover': {
                      borderColor: BRAND.accentDark,
                      backgroundColor: brandAlpha('accent', 0.1),
                    },
                    '&:disabled': {
                      borderColor: brandAlpha('accent', 0.3),
                      color: brandAlpha('accent', 0.3),
                    }
                  }}
                >
                  {t('common.back')}
                </Button>
              </Box>

              <Button
                id={activeStep === steps.length - 1 ? "store-update-submit-button" : "store-update-next-button"}
                variant="contained"
                onClick={activeStep === steps.length - 1 ? handleSubmit : handleNext}
                disabled={saving}
                endIcon={activeStep === steps.length - 1 ?
                  (saving ? <CircularProgress size={20} /> : <CheckIcon />) :
                  <ArrowForwardIcon />
                }
                sx={{
                  borderRadius: 2,
                  px: 4,
                  py: 1.5,
                  fontWeight: 700,
                  background: BRAND.accent,
                  color: BRAND.onAccent,
                  boxShadow: 'none',
                  '&:hover': {
                    background: BRAND.accentDark,
                    boxShadow: 'none',
                  },
                  '&:disabled': {
                    background: '#ccc',
                    transform: 'none',
                    boxShadow: 'none'
                  }
                }}
              >
                {saving ? t('common.submitting') :
                  activeStep === steps.length - 1 ? t('common.submit') : t('common.next')
                }
              </Button>
            </Box>
          </Box>
        </ManagementPageCard>
      {/* Success/Error Snackbar */}
      <Snackbar
        id="store-update-snackbar"
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleSnackbarClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          id={`store-update-alert-${snackbar.severity}`}
          onClose={handleSnackbarClose}
          severity={snackbar.severity}
          sx={{
            width: '100%',
            borderRadius: 2,
            fontWeight: 600
          }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
      <ConfirmDialog
        open={slugChangeDialogOpen}
        onClose={() => setSlugChangeDialogOpen(false)}
        onConfirm={handleConfirmSlugChange}
        title={t('storeUpdate.slugChangeTitle')}
        message={t('storeUpdate.slugChangeMessage')}
        confirmText={t('storeUpdate.slugChangeConfirm')}
        confirmColor="primary"
      />
    </ManagementPageLayout>
  );
};

export default StoreUpdatePage;